import type { TFunction } from 'i18next';
import { isAuthApiError } from './auth-api';

type AuthErrorContext =
    | 'default'
    | 'login'
    | 'register'
    | 'magic-link'
    | 'forgot-password'
    // Setting a new password: the reset-link form (AuthResetPassword) and the
    // change-password form in personal security settings.
    | 'reset-password'
    | 'two-factor'
    | 'invitation';

export type AuthErrorField = 'password' | 'code';

export type AuthErrorDetails = {
    message: string;
    field?: AuthErrorField;
};

const translate = (t: TFunction, key: string, defaultValue: string) =>
    String(t(key, { defaultValue }));

const normalizeCode = (code: string | undefined) =>
    code
        ?.trim()
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toUpperCase();

const normalizeCodeAlias = (code: string): string => {
    if (code.startsWith('BASE_ERROR_CODES_')) {
        return code.replace(/^BASE_ERROR_CODES_/, '');
    }

    if (code.startsWith('TWO_FACTOR_ERROR_CODES_')) {
        return code.replace(/^TWO_FACTOR_ERROR_CODES_/, '');
    }

    if (code.startsWith('ORGANIZATION_ERROR_CODES_')) {
        return code.replace(/^ORGANIZATION_ERROR_CODES_/, '');
    }

    return code;
};

const fromCode = (code: string, t: TFunction, context: AuthErrorContext): AuthErrorDetails | null => {
    switch (code) {
        case 'NETWORK_ERROR':
            return {
                message: translate(
                    t,
                    'auth.error.network',
                    'Network error. Check your connection and try again.',
                ),
            };

        case 'INVALID_EMAIL_OR_PASSWORD':
            return {
                message: translate(
                    t,
                    'auth.login.error.invalid-credentials',
                    'Wrong password or no account found. Reset your password or try a different sign-in method.',
                ),
                field: 'password',
            };

        case 'INVALID_PASSWORD':
            if (context === 'login') {
                return {
                    message: translate(
                        t,
                        'auth.login.error.invalid-credentials',
                        'Wrong password or no account found. Reset your password or try a different sign-in method.',
                    ),
                    field: 'password',
                };
            }

            return {
                message: translate(t, 'auth.error.invalid-password', 'Invalid password.'),
                field: context === 'register' ? 'password' : undefined,
            };

        case 'PASSWORD_TOO_SHORT':
            return {
                message: translate(
                    t,
                    'auth.error.password-too-short',
                    'Password is too short. Please use a longer password.',
                ),
                field: 'password',
            };

        case 'PASSWORD_TOO_LONG':
            return {
                message: translate(
                    t,
                    'auth.error.password-too-long',
                    'Password is too long. Please use a shorter password.',
                ),
                field: 'password',
            };

        case 'PASSWORD_COMPROMISED':
            return {
                message: translate(
                    t,
                    'auth.error.password-compromised',
                    'This password has appeared in a known data breach. Please choose a different password.',
                ),
                field: 'password',
            };

        case 'INVALID_EMAIL':
            return {
                message: translate(t, 'auth.error.invalid-email', 'Invalid email address.'),
            };

        case 'USER_ALREADY_EXISTS':
        case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
            return {
                message: translate(
                    t,
                    'auth.error.user-already-exists',
                    'An account with this email already exists. Try signing in instead.',
                ),
            };

        case 'EMAIL_NOT_VERIFIED':
            return {
                message: translate(
                    t,
                    'auth.error.email-not-verified',
                    'Email not verified. Check your inbox for a verification link.',
                ),
            };

        case 'EMAIL_PASSWORD_DISABLED':
        case 'EMAIL_PASSWORD_SIGN_UP_DISABLED':
            return {
                message: translate(
                    t,
                    'auth.error.email-password-disabled',
                    'Email and password sign-in is currently unavailable.',
                ),
            };

        case 'FAILED_TO_CREATE_USER':
            return {
                message: translate(
                    t,
                    'auth.error.failed-to-create-user',
                    'Could not create your account right now. Please try again.',
                ),
            };

        case 'FAILED_TO_CREATE_SESSION':
            return {
                message: translate(
                    t,
                    'auth.error.failed-to-create-session',
                    'Could not start your session. Please try again.',
                ),
            };

        case 'RESET_PASSWORD_DISABLED':
            return {
                message: translate(
                    t,
                    'auth.error.reset-password-disabled',
                    'Password reset is currently unavailable.',
                ),
            };

        case 'INVALID_TOKEN':
        case 'TOKEN_EXPIRED':
        case 'EXPIRED_TOKEN':
            return {
                message: translate(
                    t,
                    'auth.error.invalid-token',
                    'This link or token is invalid or expired. Please try again.',
                ),
            };

        case 'ATTEMPTS_EXCEEDED':
            return {
                message: translate(
                    t,
                    'auth.error.attempts-exceeded',
                    'Too many attempts for this link. Request a new one and try again.',
                ),
            };

        case 'NEW_USER_SIGNUP_DISABLED':
            return {
                message: translate(
                    t,
                    'auth.error.new-user-signup-disabled',
                    'Creating a new account with this method is currently disabled.',
                ),
            };

        case 'OAUTH_LINK_ERROR':
            return {
                message: translate(t, 'auth.oauth.error.default', 'OAuth authorization failed. Please try again.'),
            };

        case 'PROVIDER_NOT_FOUND':
            return {
                message: translate(
                    t,
                    'auth.social.error.oauth_provider_not_found',
                    'This sign-in provider is not configured. Please contact support.',
                ),
            };

        case 'ID_TOKEN_NOT_SUPPORTED':
            return {
                message: translate(
                    t,
                    'auth.error.id-token-not-supported',
                    'This provider does not support this sign-in flow.',
                ),
            };

        case 'FAILED_TO_GET_USER_INFO':
        case 'USER_EMAIL_NOT_FOUND':
            return {
                message: translate(
                    t,
                    'auth.social.error.unable_to_get_user_info',
                    "We couldn't retrieve your account info from the provider. Please try again.",
                ),
            };

        case 'CALLBACK_URL_REQUIRED':
            return {
                message: translate(
                    t,
                    'auth.error.invalid-callback-url',
                    'Invalid redirect configuration. Please try again.',
                ),
            };

        case 'INVALID_ORIGIN':
        case 'MISSING_OR_NULL_ORIGIN':
        case 'CROSS_SITE_NAVIGATION_LOGIN_BLOCKED':
            return {
                message: translate(
                    t,
                    'auth.error.security-check-failed',
                    'Security validation failed. Refresh and try again.',
                ),
            };

        case 'MISSING_RESPONSE':
        case 'VERIFICATION_FAILED':
        case 'UNKNOWN_ERROR':
        case 'CAPTCHA_CLIENT_ERROR':
        case 'CAPTCHA_SCRIPT_LOAD_FAILED':
        case 'CAPTCHA_EXECUTION_FAILED':
        case 'CAPTCHA_UNAVAILABLE':
        case 'CAPTCHA_SITE_KEY_MISSING':
            return {
                message: translate(
                    t,
                    'auth.error.captcha-verification-failed',
                    'Security verification failed. Please refresh the page and try again.',
                ),
            };

        case 'INVALID_CALLBACK_URL':
        case 'INVALID_REDIRECT_URL':
        case 'INVALID_ERROR_CALLBACK_URL':
        case 'INVALID_NEW_USER_CALLBACK_URL':
            return {
                message: translate(
                    t,
                    'auth.error.invalid-callback-url',
                    'Invalid redirect configuration. Please try again.',
                ),
            };

        case 'INVALID_CODE':
            return {
                message: translate(t, 'auth.two-factor.error.invalid', 'Invalid or expired code. Please try again.'),
                field: 'code',
            };

        case 'INVALID_TWO_FACTOR_COOKIE':
            return {
                message: translate(
                    t,
                    'auth.two-factor.error.session-expired',
                    'Your two-factor session expired. Sign in again and retry.',
                ),
            };

        case 'OTP_HAS_EXPIRED':
            return {
                message: translate(
                    t,
                    'auth.two-factor.error.expired',
                    'The verification code expired. Request a new code.',
                ),
                field: 'code',
            };

        case 'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE':
            return {
                message: translate(
                    t,
                    'auth.two-factor.error.too-many-attempts',
                    'Too many attempts. Request a new code and try again.',
                ),
                field: 'code',
            };

        case 'TOTP_NOT_ENABLED':
        case 'OTP_NOT_ENABLED':
        case 'TOTP_NOT_CONFIGURED':
        case 'OTP_NOT_CONFIGURED':
        case 'TWO_FACTOR_NOT_ENABLED':
            return {
                message: translate(
                    t,
                    'auth.two-factor.error.not-configured',
                    'Two-factor authentication is not configured for this account.',
                ),
            };

        case 'INVITATION_NOT_FOUND':
            return {
                message: translate(t, 'auth.invite.invalid', 'Invalid invitation link.'),
            };

        case 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION':
            return {
                message: translate(
                    t,
                    'auth.invite.error.not-recipient',
                    'This invitation belongs to a different email address.',
                ),
            };

        case 'EMAIL_VERIFICATION_REQUIRED_BEFORE_ACCEPTING_OR_REJECTING_INVITATION':
            return {
                message: translate(
                    t,
                    'auth.invite.error.email-verification-required',
                    'Verify your email before accepting this invitation.',
                ),
            };

        case 'ORGANIZATION_MEMBERSHIP_LIMIT_REACHED':
            return {
                message: translate(
                    t,
                    'auth.invite.error.membership-limit',
                    'This workspace has reached its member limit.',
                ),
            };

        case 'FAILED_TO_RETRIEVE_INVITATION':
            return {
                message: translate(
                    t,
                    'auth.invite.error.failed-to-retrieve',
                    'Could not load this invitation. Please try again.',
                ),
            };

        case 'ORGANIZATION_NOT_FOUND':
            return {
                message: translate(
                    t,
                    'auth.invite.error.organization-not-found',
                    'This workspace is no longer available.',
                ),
            };

        case 'INVITER_IS_NO_LONGER_A_MEMBER_OF_THE_ORGANIZATION':
            return {
                message: translate(
                    t,
                    'auth.invite.error.inviter-not-member',
                    'This invitation is no longer valid.',
                ),
            };

        case 'NOT_AUTHENTICATED':
            return {
                message: translate(
                    t,
                    'auth.invite.login-required-description',
                    'Please sign in before accepting this invitation.',
                ),
            };

        default:
            return null;
    }
};

const fromMessage = (message: string, t: TFunction, context: AuthErrorContext): AuthErrorDetails | null => {
    const lowered = message.toLowerCase();

    if (
        context === 'login' &&
        /invalid.*(email|password)|wrong.*password|incorrect.*password|invalid.*credentials|no.*account|user.*not.*found/.test(
            lowered,
        )
    ) {
        return {
            message: translate(
                t,
                'auth.login.error.invalid-credentials',
                'Wrong password or no account found. Reset your password or try a different sign-in method.',
            ),
            field: 'password',
        };
    }

    if (/password.*too short/.test(lowered)) {
        return {
            message: translate(
                t,
                'auth.error.password-too-short',
                'Password is too short. Please use a longer password.',
            ),
            field: 'password',
        };
    }

    if (/password.*too long/.test(lowered)) {
        return {
            message: translate(
                t,
                'auth.error.password-too-long',
                'Password is too long. Please use a shorter password.',
            ),
            field: 'password',
        };
    }

    if (/compromised|data breach|pwned/.test(lowered)) {
        return {
            message: translate(
                t,
                'auth.error.password-compromised',
                'This password has appeared in a known data breach. Please choose a different password.',
            ),
            field: 'password',
        };
    }

    if (/captcha|recaptcha/.test(lowered)) {
        return {
            message: translate(
                t,
                'auth.error.captcha-verification-failed',
                'Security verification failed. Please refresh the page and try again.',
            ),
        };
    }

    if (/invitation not found/.test(lowered)) {
        return {
            message: translate(t, 'auth.invite.invalid', 'Invalid invitation link.'),
        };
    }

    if (context === 'invitation' && /not authenticated|unauthorized/.test(lowered)) {
        return {
            message: translate(
                t,
                'auth.invite.login-required-description',
                'Please sign in before accepting this invitation.',
            ),
        };
    }

    return null;
};

export const mapAuthError = (
    error: unknown,
    t: TFunction,
    context: AuthErrorContext = 'default',
): AuthErrorDetails => {
    if (isAuthApiError(error)) {
        const code = normalizeCode(error.code);
        if (code) {
            const fromKnownCode = fromCode(normalizeCodeAlias(code), t, context);
            if (fromKnownCode) {
                return fromKnownCode;
            }
        }

        if (error.status === 401 && context === 'invitation') {
            return {
                message: translate(
                    t,
                    'auth.invite.login-required-description',
                    'Please sign in before accepting this invitation.',
                ),
            };
        }

        if (error.status === 429) {
            return {
                message: translate(
                    t,
                    'auth.error.rate-limited',
                    'Too many attempts. Please wait and try again.',
                ),
            };
        }

        if (error.status !== undefined && error.status >= 500) {
            return {
                message: translate(
                    t,
                    'auth.error.server',
                    'Server error. Please try again in a moment.',
                ),
            };
        }

        const fromKnownMessage = fromMessage(error.message, t, context);
        if (fromKnownMessage) {
            return fromKnownMessage;
        }

        return {
            message: error.message || translate(t, 'error.something-wrong', 'Something went wrong. Please try again'),
        };
    }

    if (error instanceof Error && error.message) {
        const fromKnownMessage = fromMessage(error.message, t, context);
        if (fromKnownMessage) {
            return fromKnownMessage;
        }

        return { message: error.message };
    }

    return {
        message: translate(t, 'error.something-wrong', 'Something went wrong. Please try again'),
    };
};
